import { ExternalLink, PenSquare } from "lucide-react";
import { GOOGLE_REVIEWS_READ_URL, GOOGLE_REVIEWS_WRITE_URL } from "@/utils/googleReviews";

/**
 * Canonical READ-reviews CTA. Always links to the Place-ID-bound Google local
 * reviews list. Never points at a Google search URL.
 *
 * @param {"primary"|"secondary"|"link"} variant  Visual weight.
 * @param {number} count  Optional live review count. Shapes label when ≥10 ("Read 40+ customer reviews").
 * @param {string} className
 * @param {string} testId
 */
export function ReviewCTARead({ variant = "primary", count = 0, className = "", testId = "review-cta-read" }) {
  const label =
    count >= 10
      ? `Read ${Math.floor(count / 10) * 10}+ customer reviews`
      : "See all Google reviews";

  const base = "inline-flex items-center gap-2 font-semibold rounded-full transition-colors";
  const styles = {
    primary: "bg-brand-black hover:bg-brand-aqua-dark text-white px-6 py-2.5 text-sm",
    secondary: "bg-white border border-brand-light-grey hover:border-brand-aqua text-brand-black px-6 py-2.5 text-sm",
    link: "text-brand-aqua hover:text-brand-aqua-dark underline underline-offset-4 text-sm px-0 py-0",
  };

  return (
    <a
      href={GOOGLE_REVIEWS_READ_URL}
      target="_blank"
      rel="noopener noreferrer"
      className={`${base} ${styles[variant]} ${className}`}
      data-testid={testId}
      aria-label={label}
    >
      {label}
      {variant !== "link" && <ExternalLink size={14} />}
    </a>
  );
}

/**
 * Canonical WRITE-review CTA. Always links to Google's "Leave a review" dialog
 * pre-bound to our Place ID.
 *
 * Placement rules:
 *  - Marketing surfaces (homepage banner): use variant="link" — subtle secondary nudge only.
 *  - Post-service thank-you surfaces (none today in this app): use variant="primary".
 *  - Never place next to a READ CTA with equal visual weight — that splits intent.
 *
 * @param {"primary"|"secondary"|"link"} variant  Visual weight.
 * @param {string} className
 * @param {string} testId
 */
export function ReviewCTAWrite({ variant = "link", className = "", testId = "review-cta-write" }) {
  const label = "Leave a review";
  const base = "inline-flex items-center gap-1.5 font-semibold rounded-full transition-colors";
  const styles = {
    primary: "bg-brand-aqua hover:bg-brand-aqua-dark text-white px-6 py-2.5 text-sm",
    secondary: "bg-white border border-brand-light-grey hover:border-brand-aqua text-brand-black px-6 py-2.5 text-sm",
    link: "text-xs text-brand-grey hover:text-brand-black underline-offset-4 hover:underline rounded-none px-0 py-0",
  };
  return (
    <a
      href={GOOGLE_REVIEWS_WRITE_URL}
      target="_blank"
      rel="noopener noreferrer"
      className={`${base} ${styles[variant]} ${className}`}
      data-testid={testId}
      aria-label={label}
    >
      {variant === "primary" && <PenSquare size={14} />}
      {label}
    </a>
  );
}
